<template>
  <div class="studio-product-content">
    <StudioProductPlaylists :showOnlyFeatured="true" />
    <div class="row">
      <div class="col-12">
        <HMSearch v-model="filterBy.q" @input="filterMedia" />
      </div>
    </div>
    <div class="row mt-2 d-none d-md-flex">
      <div
        class="col-12 d-flex flex-row align-items-center justify-content-start flex-nowrap"
      >
        <b-form-select v-model="filterBy.mediaType" @change="filterMedia">
          <b-form-select-option :value="null">{{
            $t('PRODUCTS.LABELS.FILTER_ALL_TYPES')
          }}</b-form-select-option>
          <b-form-select-option
            v-for="mt in mediaTypes"
            :value="mt.value"
            :key="mt.value"
          >
            {{ $t(mt.displayCode) }}
          </b-form-select-option>
        </b-form-select>
        <b-form-select
          v-model="filterBy.instructor"
          class="ml-2"
          @change="filterMedia"
        >
          <b-form-select-option :value="null">{{
            $t('PRODUCTS.LABELS.FILTER_INSTRUCTORS')
          }}</b-form-select-option>
          <b-form-select-option
            v-for="instructor in product.instructors"
            :value="instructor.id"
            :key="instructor.id"
          >
            {{ instructor.name }}
          </b-form-select-option>
        </b-form-select>
        <b-form-select
          v-for="category in getProductFilterOptions"
          :key="category.category_id"
          v-model="filterBy.filterByCategories[category.category_id]"
          class="ml-2"
          @change="filterMedia"
        >
          <b-form-select-option :value="null">{{
            category.name
          }}</b-form-select-option>
          <b-form-select-option
            v-for="option in category.option_list"
            :value="option.id"
            :key="option.id"
          >
            {{ option.name }}
          </b-form-select-option>
        </b-form-select>
      </div>
    </div>
    <div class="row mt-2 d-md-none">
      <div
        class="col-12 d-flex flex-row align-items-center justify-content-start flex-wrap"
      >
        <b-form-select
          class="mt-1 w-100"
          v-model="filterBy.mediaType"
          @change="filterMedia"
        >
          <b-form-select-option :value="null">{{
            $t('PRODUCTS.LABELS.FILTER_ALL_TYPES')
          }}</b-form-select-option>
          <b-form-select-option
            v-for="mt in mediaTypes"
            :value="mt.value"
            :key="mt.value"
          >
            {{ $t(mt.displayCode) }}
          </b-form-select-option>
        </b-form-select>
        <b-form-select
          class="mt-1 w-100"
          v-model="filterBy.instructor"
          @change="filterMedia"
        >
          <b-form-select-option :value="null">{{
            $t('PRODUCTS.LABELS.FILTER_INSTRUCTORS')
          }}</b-form-select-option>
          <b-form-select-option
            v-for="instructor in instructors"
            :value="instructor.id"
            :key="instructor.id"
          >
            {{ instructor.name }}
          </b-form-select-option>
        </b-form-select>
        <b-form-select
          class="mt-1 w-100"
          v-for="category in getProductFilterOptions"
          :key="category.category_id"
          v-model="filterBy.filterByCategories[category.category_id]"
          @change="filterMedia"
        >
          <b-form-select-option :value="null">{{
            category.name
          }}</b-form-select-option>
          <b-form-select-option
            v-for="option in category.option_list"
            :value="option.id"
            :key="option.id"
          >
            {{ option.name }}
          </b-form-select-option>
        </b-form-select>
      </div>
    </div>
    <HMPaginatedTable
      v-if="query"
      ref="product-content-table"
      api="product/getProductMediaPaginated"
      :query="query"
      name="product-content-table"
      :pageInQuery="true"
      :perPageFixed="12"
    >
      <template slot-scope="{ data }">
        <div class="row mt-4">
          <div
            class="column col-6 col-md-4 col-lg-3 mt-2"
            :class="{ review: review }"
            v-for="media in data.res.results"
            :key="media.id"
          >
            <router-link
              class="undecorated-router-link"
              :to="
                getCorrectRoute({
                  media_id: media.id,
                  isMediaLocked: media.media.locked,
                })
              "
            >
              <StudioMediaCard :product="product" :media="media" />
            </router-link>
          </div>
        </div>
      </template>
    </HMPaginatedTable>

    <div class="row" v-if="showEmpty">
      <div class="col-12 text-center py-5">
        {{ $t('PRODUCTS.TEXT.NO_MEDIA_FOUND') }}
      </div>
    </div>
    <div class="row" v-if="isTableLoading">
      <div class="col-12 text-center py-5">
        <HMSpinner />
      </div>
    </div>
    <div class="page-bottom-filler"></div>
  </div>
</template>

<script>
import Vue from 'vue';
import { errorToMessage } from '@/core/utils/errors';
import { mapGetters } from 'vuex';
import StudioMediaCard from '@/web/components/products/StudioMediaCard';
import { mediaTypes } from '@/core/utils/products';
import RefreshData from '@/web/mixins/refreshData';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import StudioProductPlaylists from '@/web/components/products/StudioProductPlaylists';

export default Vue.extend({
  name: 'StudioProductContent',
  mixins: [RefreshData],
  components: {
    StudioMediaCard,
    StudioProductPlaylists,
  },
  data() {
    return {
      isLoading: true,
      mediaTypes: [],
      filterBy: {
        q: null,
        mediaType: null,
        instructor: null,
        filterByCategories: {},
      },
      query: null,
    };
  },
  props: {
    review: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      product: 'product/product',
      productMedia: 'product/productMedia',
      instructors: 'product/studioInstructors',
    }),
    getProductFilterOptions() {
      return this.product.categories;
    },
    studioURL() {
      return getStudioURL(this.$route);
    },
    productId() {
      return this.$route?.params?.product_id;
    },
    table() {
      return this.$store.state['product-content-table'];
    },
    showEmpty() {
      if (this.table) {
        return this.table.perPage === 0 && !this.table.isLoading;
      }
      return false;
    },
    isTableLoading() {
      const table = this.$store.state['product-content-table'];
      if (table) {
        return table.isLoading;
      }
      return false;
    },
  },
  methods: {
    filterOptionsIds() {
      if (this.filterBy.filterByCategories) {
        return Object.values(this.filterBy.filterByCategories)
          .map(r => r)
          .filter(r => r);
      }
      return [];
    },
    filterMedia() {
      this.query = this.searchQuery();
    },
    searchQuery() {
      return {
        studioURL: this.studioURL,
        productId: this.productId,
        options: this.filterOptionsIds(),
        instructor: this.filterBy.instructor,
        mediaType: this.filterBy.mediaType,
        q: this.filterBy.q,
      };
    },
    getCorrectRoute({ media_id, isMediaLocked }) {
      if (isMediaLocked) {
        return {
          name: 'studio-product-checkout',
          params: { product_id: this.$route.params.product_id },
        };
      }
      return {
        name: 'studio-product-media',
        params: { media_id },
      };
    },
    async loadData() {
      const studioURL = this.studioURL;
      try {
        this.isLoading = true;
        await Promise.all([
          this.$store.dispatch('product/getStudioInstructors', { studioURL }),
          this.$store.dispatch('product/getFilterCategories', { studioURL }),
        ]);
        const filterCategories = {};
        this.getProductFilterOptions.forEach(category => {
          filterCategories[category.category_id] = null;
        });
        this.filterBy.filterByCategories = filterCategories;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load information',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    await this.loadData();
    this.mediaTypes = mediaTypes();
    const filter = await this.$store.dispatch(
      'filters/getFilter',
      'studio-product-content'
    );
    if (filter && filter.productId === this.productId) {
      this.filterBy = filter.filterBy;
    }
    this.query = this.searchQuery();
  },
  beforeDestroy() {
    this.$store.dispatch('filters/setFilter', {
      key: 'studio-product-content',
      filter: {
        productId: this.query.productId,
        filterBy: JSON.parse(JSON.stringify(this.filterBy)),
      },
    });
  },
});
</script>

<style scoped>
.review {
  pointer-events: none;
}
</style>
